import React from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { MODEL_FAMILIES } from "../utils/constants";
import "./CreditsBadge.css";

/**
 * Compact credit balance display for the dashboard header.
 *
 * Shows per-family balances (H/S/O) with color coding:
 *   - green: healthy (above threshold, or null = unlimited)
 *   - yellow: low (at or below threshold, but > 0)
 *   - red: zero / exhausted
 *
 * Clicking navigates to Settings page (billing/credits section).
 *
 * @param {{ credits: Object|null, lowBalanceWarnings: string[], loading: boolean, error: string|null }} props
 */
const CreditsBadge = ({ credits, lowBalanceWarnings = [], loading, error }) => {
  const navigate = useNavigate();

  if (error && !credits) {
    return (
      <div
        className="credits-badge credits-badge--error"
        title="Failed to load credits"
      >
        <span className="credits-badge__dot">!</span>
      </div>
    );
  }

  if (loading || !credits) {
    return (
      <div
        className="credits-badge credits-badge--loading"
        title="Loading credits..."
        role="status"
        aria-label="Loading credit balances"
      >
        <span className="credits-badge__dot">...</span>
      </div>
    );
  }

  const getStatus = (family) => {
    const balance = credits[family];
    if (balance == null) return "healthy"; // null = unlimited
    if (balance === 0) return "exhausted";
    if (lowBalanceWarnings.includes(family)) return "low";
    return "healthy";
  };

  const formatBalance = (balance) => {
    if (balance == null) return "\u221e"; // null = unlimited → ∞
    if (balance >= 10000) return Math.floor(balance / 1000) + "k";
    return String(balance);
  };

  return (
    <button
      className="credits-badge"
      onClick={() => navigate("/settings#credits")}
      title="View credit balances"
      aria-label="Credit balances — click to view details"
    >
      {MODEL_FAMILIES.map(({ key, shortLabel }, idx) => (
        <React.Fragment key={key}>
          {idx > 0 && <span className="credits-badge__sep">|</span>}
          <span
            className={`credits-badge__item credits-badge__item--${getStatus(key)}`}
          >
            <span className="credits-badge__label">{shortLabel}:</span>
            <span className="credits-badge__value">
              {formatBalance(credits[key])}
            </span>
          </span>
        </React.Fragment>
      ))}
    </button>
  );
};

CreditsBadge.propTypes = {
  credits: PropTypes.object,
  lowBalanceWarnings: PropTypes.arrayOf(PropTypes.string),
  loading: PropTypes.bool,
  error: PropTypes.string,
};

export default CreditsBadge;
